<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { SiteSectionsTreeSectionCategoryFilterOption } from "@/app/tools/new-section-management/site-sections-tree/filters/section-category-filter/site-sections-tree-section-category-filter.types";
import { filterSectionForest } from "@/app/tools/new-section-management/site-sections-tree/filters/section-tree-filtering";
import { ISiteSectionsVirtualizedTableNode } from "@/components/site/site-sections-virtualized-table/site-sections-virtualized-table.types";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { SiteSectionCategoryId } from "@/data/common/ids";
import { SectionNodePlanning } from "@/domains/v4b/site-programming-range/site-programming-range.types";
import { useTypedIntl } from "@/locales/typed-intl";
import router from "@/router/router";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";

import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";

import SiteSectionsTreeSectionCategoryFilter from "@/app/tools/new-section-management/site-sections-tree/filters/section-category-filter/site-sections-tree-section-category-filter.vue";

const currentRouteName = computed(() => router.currentRoute.value.name);

const props = defineProps<{
  mappedSiteSectionTree: ISiteSectionsVirtualizedTableNode<SectionNodePlanning>[];
  sectionCategoryFilterOptions: SiteSectionsTreeSectionCategoryFilterOption[];
}>();

const emits = defineEmits<{
  (e: "filter-section-tree", result: ISiteSectionsVirtualizedTableNode<SectionNodePlanning>[]): void;
}>();

const { t, k } = useTypedIntl();

const selectedSectionCategoryIds = defineModel<SiteSectionCategoryId[]>("selectedSectionCategoryIds", {
  required: true,
});
const sectionSearchQuery = ref<string>("");

const filteredMappedSiteSectionTree = computed(() => {
  return filterSectionForest(props.mappedSiteSectionTree, {
    searchQuery: sectionSearchQuery.value,
    selectedSectionCategories: selectedSectionCategoryIds.value,
  });
});

function handleSearchSectionQueryChanged() {
  MixpanelService.trackEvent(EMixpanelEvent.PLANNING_SECTION_SEARCH_PERFORMED);
}

function handleSelectedSectionCategoriesChanged(newSelectedSectionCategoryIds: SiteSectionCategoryId[]) {
  if (newSelectedSectionCategoryIds.length) {
    MixpanelService.trackEvent(EMixpanelEvent.SECTION_CATEGORY_FILTER_APPLIED, {
      category_id: newSelectedSectionCategoryIds,
      page_context: currentRouteName.value?.toString(),
    });
  } else {
    MixpanelService.trackEvent(EMixpanelEvent.SECTION_CATEGORY_FILTER_CLEARED, {
      page_context: currentRouteName.value?.toString(),
      method: "dropdown",
    });
  }
}

watch(
  filteredMappedSiteSectionTree,
  (newVal) => {
    emits("filter-section-tree", newVal);
  },
  { immediate: true },
);
</script>

<template>
  <div class="contents">
    <IconField
      iconPosition="left"
      class="flex flex-1 w-full"
    >
      <InputIcon class="pi pi-search" />
      <InputText
        v-model.trim="sectionSearchQuery"
        class="w-full !text-sm min-w-[220px]"
        :data-test-id="DATA_TEST_IDS.views.siteSectionsPlanning.filters.searchInputText"
        :placeholder="t(k.tools.siteManagement.siteTreeStructure.searchSection)"
        :pt="{
          root: {
            class: '!h-[42px]',
          },
        }"
        @value-change="handleSearchSectionQueryChanged"
      />
    </IconField>

    <SiteSectionsTreeSectionCategoryFilter
      v-model:selected-categories="selectedSectionCategoryIds"
      :options="props.sectionCategoryFilterOptions"
      @update:selected-categories="handleSelectedSectionCategoriesChanged"
    />
  </div>
</template>
